import { useState, useCallback, useMemo, useContext } from 'react';
import { AuthContext } from 'contexts';
import { useQueryClient } from '@tanstack/react-query';
import * as auth from '../helpers/auth';
import { useApp } from './AppProvider';

const getAccountStatus = account => {
  const isSubscribed = !!account && !!account.stripeData.subscription;

  const isOnboarded = !!account && !!account.isOnboarded;

  return { isSubscribed, isOnboarded };
};

const defaultState = {
  user: null,
  account: null,
  jwt: null,
};

const useInitFromAppState = () => {
  const jwt = window.localStorage.getItem('global-opportunities-jwt');
  const {
    apiRegions,
    timezone,
    email,
    firstName,
    lastName,
    isLoggedIn,
    vendorUUID,
    isConfirmed,
    stripeCustomerId,
    subscriptionId,
    id,
  } = useApp();

  return {
    ...defaultState,
    user: isLoggedIn
      ? {
          email,
          firstName,
          lastName,
          isConfirmed,
          id,
          timezone,
        }
      : null,
    account: vendorUUID
      ? {
          vendor: { id: `vdr_${vendorUUID}` },
          stripeData: {
            subscription: subscriptionId ? { id: subscriptionId } : null,
            customer: stripeCustomerId ? { id: stripeCustomerId } : null,
          },
        }
      : null,
    apiRegions,
    jwt,
  };
};

const useProvideAuth = () => {
  const initialState = useInitFromAppState();
  const { onLoginSuccess, onLogoutSuccess } = useApp();
  const queryClient = useQueryClient();
  const [{ user, account, jwt }, setAuth] = useState(initialState);

  const login = useCallback(
    (email, password) =>
      auth.login(email, password).then(data => {
        setAuth(data);

        // `setAuth` triggers a state update so we need to wait for that before immediately
        // updating the rest of the app state and local storage.
        let timeout = null;
        timeout = setTimeout(() => {
          if (data.user) {
            const { isSubscribed, isOnboarded } = getAccountStatus(data.account);
            const legacyVendorUuid = data.account ? data.account.vendor.id.substr(4) : null;

            onLoginSuccess({
              ...data.user,
              isSubscribed,
              isOnboarded,
              vendors: legacyVendorUuid ? [legacyVendorUuid] : null,
              vendorUUID: legacyVendorUuid,
              subscriptionId: isSubscribed ? data.account.stripeData.subscription.id : null,
              stripeCustomerId:
                data.account && data.account.stripeData.customer
                  ? data.account.stripeData.customer.id
                  : null,
              apiRegions: data.apiRegions,
            });
          }
          clearTimeout(timeout);
        });
      }),
    [setAuth, onLoginSuccess]
  );

  const logout = useCallback(() => {
    auth.logout();
    onLogoutSuccess();
    setAuth({ ...defaultState });
    // Defer slightly so avoid immediate refetch of feature flags
    setTimeout(() => {
      queryClient.resetQueries();
    });
  }, [setAuth, onLogoutSuccess, queryClient]);

  const confirm = useCallback(
    token =>
      auth.confirmAccount(token).then(() => {
        if (user !== null) {
          setAuth({
            user: { ...user, isConfirmed: true },
            account,
            jwt,
          });
        }
      }),
    [setAuth, user, account, jwt]
  );

  const subscribe = useCallback(
    ({ subscriptionId, customerId }) => {
      setAuth({
        user,
        jwt,
        account: {
          ...account,
          stripeData: {
            subscription: { id: subscriptionId },
            customer: { id: customerId },
          },
        },
      });
    },
    [setAuth, user, account, jwt]
  );

  const register = useCallback(async (registrationToken, data) => {
    return auth.register(registrationToken, data);
  }, []);

  return useMemo(() => {
    const { isSubscribed, isOnboarded } = getAccountStatus(account);

    return {
      user,
      account,
      jwt,

      isSubscribed,
      isOnboarded,

      login,
      logout,
      confirm,
      subscribe,
      register,
    };
  }, [user, account, jwt, login, logout, confirm, subscribe, register]);
};

const AuthProvider = props => {
  const value = useProvideAuth();

  return <AuthContext.Provider value={value} {...props} />;
};

const useAuth = () => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('Cannot call useAuth outside of an AuthProvider');
  }

  return context;
};

export { AuthProvider, useAuth };
